import { useMutation, useQueryClient, type QueryClient } from '@tanstack/vue-query';
import { request } from '@/api/request';
import { mergeMutationOptions, type MutationOptions } from '@/modules/query/utils';
import type { ModificationType } from '@@/packages/shared/types/models/modification';
import { SUPPLIERS_QUERY } from '@/modules/connection-view-suppliers/api/queryKeys';
import { CLIENT_DASHBOARD_QUERY } from '@/modules/client-dashboard/api/queryKeys';
import type { ComplianceStatus } from './types';
import type { GetSupplierComplianceResponseV2 } from './supplier';
import {
  SUPPLIER_ASSESSMENT_OVERRIDES_QUERY,
  SUPPLIER_INFO_QUERY,
  SUPPLIER_COMPLIANCE_VTWO_QUERY,
} from './queryKeys';

type ApplyOverrideVariables = {
  supplierID: string;
  controlID: string;
  productID?: string;
  reason: string;
  type: ModificationType;
};

type ApplyOverrideResponse = {
  type: ModificationType;
  controlID: string;
  productID?: string;
  reason: string;
  createdAt: string;
  createdBy: string;
};

// Compliance recalc on the backend is event-driven (POST /overrides writes the
// modification row and publishes OverrideApplied; a worker subscriber then
// recomputes connection-level compliance). Without waiting, an immediate
// refetch races the worker and the FE locks onto stale "Not scored" data.
// Rather than predict the post-override compliance status on the FE — the BE's
// recalc can land on OutOfScope / FrameworkNotRequired / etc. depending on
// scoping and policy state — we wait for the BE's own value to change.
const COMPLIANCE_DRAIN_MAX_ATTEMPTS = 12;
const COMPLIANCE_DRAIN_INTERVAL_MS = 250;

function getControlStatus(
  data: GetSupplierComplianceResponseV2 | undefined,
  controlID: string,
  productID: string | null,
): ComplianceStatus | undefined {
  return data?.controlCompliances?.find(
    (cc) => cc.controlID === controlID && cc.productID === productID,
  )?.complianceStatus;
}

async function fetchCompliance(supplierID: string) {
  const res = await request<GetSupplierComplianceResponseV2>(
    'GET',
    `/suppliers/${supplierID}/compliance/v2`,
  );
  return res.data;
}

// Refetches the supplier compliance query until the given control's status
// differs from `beforeStatus`, or attempts run out. Populates the query cache
// each iteration so observers see the final value without an extra fetch.
async function waitForControlStatusChange(
  queryClient: QueryClient,
  supplierID: string,
  controlID: string,
  productID: string | null,
  beforeStatus: ComplianceStatus | undefined,
  attemptsLeft = COMPLIANCE_DRAIN_MAX_ATTEMPTS,
): Promise<void> {
  const fresh = await fetchCompliance(supplierID);
  queryClient.setQueryData([SUPPLIER_COMPLIANCE_VTWO_QUERY, supplierID], fresh);
  if (getControlStatus(fresh, controlID, productID) !== beforeStatus) {
    return;
  }
  if (attemptsLeft <= 1) {
    return;
  }
  await new Promise((resolve) => {
    setTimeout(resolve, COMPLIANCE_DRAIN_INTERVAL_MS);
  });
  return waitForControlStatusChange(
    queryClient,
    supplierID,
    controlID,
    productID,
    beforeStatus,
    attemptsLeft - 1,
  );
}

export function useApplyOverride(
  options?: MutationOptions<ApplyOverrideResponse, ApplyOverrideVariables>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(override) {
        const productID = override.productID ?? null;
        const before = queryClient.getQueryData<GetSupplierComplianceResponseV2>([
          SUPPLIER_COMPLIANCE_VTWO_QUERY,
          override.supplierID,
        ]);
        const beforeStatus = getControlStatus(before, override.controlID, productID);

        const res = await request<ApplyOverrideResponse>(
          'POST',
          `/overrides/${override.supplierID}/${override.controlID}`,
          {
            reason: override.reason,
            type: override.type,
            productID: override.productID || undefined,
          },
        );

        await waitForControlStatusChange(
          queryClient,
          override.supplierID,
          override.controlID,
          productID,
          beforeStatus,
        );

        return res.data;
      },
      async onSuccess(_data, { supplierID }) {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [SUPPLIER_ASSESSMENT_OVERRIDES_QUERY, supplierID],
          }),
          queryClient.invalidateQueries({
            queryKey: [SUPPLIER_INFO_QUERY, supplierID],
          }),
          queryClient.invalidateQueries({ queryKey: [SUPPLIERS_QUERY] }),
          queryClient.invalidateQueries({ queryKey: [CLIENT_DASHBOARD_QUERY] }),
        ]);
      },
    }),
  );
}

export function useRemoveOverride(options?: MutationOptions<void, string>) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(overrideID) {
        const res = await request<void>('DELETE', `/overrides/${overrideID}`);
        return res.data;
      },
      async onSuccess(_data) {
        await queryClient.invalidateQueries({
          queryKey: [SUPPLIER_ASSESSMENT_OVERRIDES_QUERY],
        });
        // TODO: mirror the post-mutate poll that useApplyOverride does — needs
        // the (supplierID, controlID) pair so we can wait for the specific
        // control's status to change. The current DELETE /overrides/:id route
        // doesn't expose that to the FE; either change the request shape or
        // have the BE return the affected supplierID+controlID.
        await new Promise((resolve) => {
          setTimeout(resolve, 500);
        });
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [SUPPLIER_INFO_QUERY] }),
          queryClient.invalidateQueries({ queryKey: [SUPPLIER_COMPLIANCE_VTWO_QUERY] }),
          queryClient.invalidateQueries({ queryKey: [SUPPLIERS_QUERY] }),
          queryClient.invalidateQueries({ queryKey: [CLIENT_DASHBOARD_QUERY] }),
        ]);
      },
    }),
  );
}
